<script setup lang="ts">
import "@cisco-u/tags/cu-tags.js";
import "@cisco-u/button/cu-button.js";
import "@cisco-u/new-card/cu-new-card.js";
import "@cisco-u/icon-garden/cu-icon-garden.js";
import "@cisco-u/icons/icons/cu-icon-spiked-circle.js";
import "@cisco-u/icons/icons/cu-icon-curved-rectangle.js";
import "@cisco-u/icons/icons/cu-icon-color-stars.js";
import "@cisco-u/icons/icons/cu-icon-lab.js";

import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import AnchorTargetBlank from "@/components/AnchorTargetBlank.vue";
import RawHtmlBlock from "@/components/RawHtmlBlock.vue";
import tagList from "@/components/tagList.vue";
import { useCardActions } from "@/composables/useCardActions";
import { type CardProps, useCardData } from "@/composables/useCardData";
import { useCardIcons } from "@/composables/useCardIcons";
import { usePricing } from "@/composables/usePricing";
import { useRevUpCampaign } from "@/composables/useRevUpCampaign";
import { progressCalc } from "@/utils/contentProgress";

interface Props extends CardProps {
  cy: string;
  link?: string | number;
  purchaseType?: string;
  showDescription?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isUnauth: false,
  cardView: "Grid",
  cardStyle: "Grid",
  link: "",
  isRemovable: false,
  isAdminContent: false,
  showDescription: true,
});

const emit = defineEmits<{
  navigate: [result: any];
  bookmark: [result: any];
  ctaClick: [result: any];
  removeProgress: [result: any];
}>();

const store = useStore();
const cardEl = ref<HTMLElement>();

// Use composables
const cardData = useCardData(props);
const pricing = usePricing(
  props,
  cardData.prices,
  cardData.isStoreAllProductsPage
);
const cardActions = useCardActions(props, emit);
const cardIcons = useCardIcons(
  cardData.cardData,
  cardData.essentialsContent,
  cardData.allAccessContent,
  cardData.freeContent,
  cardData.routerName.value
);

// RevUp campaign logic
const hasAccessToCampaign = (): boolean => {
  return (
    props.isUnauth ||
    (cardData.hasFree.value &&
      (cardData.essentialsContent.value || cardData.allAccessContent.value)) ||
    (cardData.hasEssentials.value && cardData.allAccessContent.value)
  );
};

const revUpCampaign = useRevUpCampaign(
  props,
  cardData.revupCampaigns,
  hasAccessToCampaign
);

// Additional computed properties specific to theCard
const eventDate = computed(() => {
  if (props.result?.type == "webinar") {
    return cardData.cardData.value.date;
  }
  return null;
});

const achievements = computed(() => store.getters["user/achievements"]);

const progressPercentage = computed((): number | string => {
  if (!props.result || props.isUnauth) return 0;

  const resultType = ["path", "tutorial", "course", "challenge"].includes(
    props.result.type || ""
  )
    ? props.result.type
    : "course";

  if (resultType === "tutorial") {
    return (
      progressCalc(props.result, achievements.value, resultType || "course") ||
      progressCalc(props.result, achievements.value, "course")
    );
  }
  if (resultType === "challenge") {
    const progress = progressCalc(
      props.result,
      achievements.value,
      resultType || "course"
    );
    return progress == 100 ? "Completed" : "";
  }

  return progressCalc(props.result, achievements.value, resultType || "course");
});

// Override isLocked for theCard specific logic
const isLocked = computed((): boolean => {
  // Standalone labs should show as locked for unauth users
  if (
    props.result?.type === "lab" &&
    props.result?.lab_type === "standalone-labs"
  ) {
    return props.isUnauth;
  }

  if (revUpCampaign.isRevup.value) {
    return !revUpCampaign.isRevup.value;
  }

  if (window.env.ENABLE_LOCK_CONTENT) {
    return Boolean(props.result?.lock_content);
  }

  if (cardData.freeContent.value) {
    return !cardData.freeContent.value;
  }

  return cardData.cardData.value.premium;
});

// Override filteredGarden for theCard
const filteredGarden = computed(() => {
  return (
    cardData.cardData.value?.cardGarden?.filter(
      (g: any) => g.type !== "globe"
    ) ?? []
  );
});

// Additional computed property for bundle/exam voucher check
const isBundleOrExamVoucher = computed((): boolean => {
  return (
    props.result?.type === "bundle" || props.result?.type === "exam-voucher"
  );
});

const hasEntitlement = computed((): boolean => {
  const userEntitlements = store.getters["user/entitlements"] || [];
  const productGuid = pricing.priceDetails.value?.productGuid;
  const labGuid = props.result?.guid;

  if (!productGuid && !labGuid) return false;

  // Check if user has entitlement by matching:
  // 1. product_id (for older entitlements that have it)
  // 2. content_bundle.guid (for bundle/course/lab entitlements)
  // 3. For labs: match by lab guid (single title) or global_products content_bundle_id (multi-title)
  return userEntitlements.some((entitlement: any) => {
    // Match by product_id if it exists and is not 0
    if (
      productGuid &&
      entitlement?.product_id &&
      entitlement.product_id !== 0 &&
      entitlement.product_id === productGuid
    ) {
      return true;
    }

    // For labs: match by lab guid (single title entitlement)
    if (
      props.result?.type === "lab" &&
      labGuid &&
      entitlement?.content_bundle?.is_single === 1 &&
      entitlement?.content_bundle?.guid === labGuid
    ) {
      return true;
    }

    // For labs: match by content_bundle_id (multi-title entitlement like All Access)
    if (props.result?.type === "lab" && props.result?.global_products) {
      const labContentBundleIds = props.result.global_products
        .map((p: any) => p.content_bundle_id)
        .filter((id: any) => id);

      if (
        entitlement?.content_bundle?.id &&
        labContentBundleIds.includes(entitlement.content_bundle.id)
      ) {
        return true;
      }
    }

    // Match by content_bundle.guid for other content types
    if (entitlement?.content_bundle?.guid === productGuid) {
      return true;
    }

    return false;
  });
});

const isCMLLab = computed((): boolean => {
  return (
    props.result?.type === "lab" &&
    props.result?.lab_type === "cisco-modeling-labs"
  );
});

const isStandaloneLab = computed((): boolean => {
  return (
    props.result?.type === "lab" && props.result?.lab_type === "standalone-labs"
  );
});

const isGradedLab = computed((): boolean => {
  return (
    props.result?.type === "lab" && props.result?.lab_type === "graded-labs"
  );
});

const footerDescription = computed((): string => {
  if (isStandaloneLab.value) {
    // For unauth, return empty to let cu-new-card show its built-in "View subscription options" with lock icon
    return props.isUnauth ? "" : "Launch lab";
  }
  if (isCMLLab.value) return "";
  if (!isBundleOrExamVoucher.value) return "";
  if (hasEntitlement.value) return "";
  return "View purchase options";
});

// Override purchaseType for standalone labs in unauth mode to show subscription footer with lock icon
const cardPurchaseType = computed((): string | undefined => {
  if (isStandaloneLab.value && props.isUnauth) {
    return "subscription";
  }
  return props.purchaseType;
});

onMounted(() => {
  if (cardEl.value) {
    cardActions.setupIntersectionObserver(cardEl.value);
  }
});
</script>

<template>
  <div data-cy="card-cu" ref="cardEl" class="h-full">
    <cu-new-card
      v-if="cardActions.isInViewPort.value && !props.isUnauth"
      :class="[
        {
          'h-full': cardData.storePlansPage.value,
          'h-full min-h-[22rem]': props.forCarousel,
          'h-[22rem]': !cardData.storePlansPage.value && !props.forCarousel,
        },
      ]"
      :type="cardData.cardData.value.type"
      :bookmarked="cardData.inMyList.value(props.result)"
      :progress="progressPercentage || revUpCampaign.freeUntil.value"
      :removable="cardData.canItBeRemoved.value"
      :admin="props.isAdminContent"
      :premium="
        ['exam-voucher', 'bundle', 'bundle-3', 'bundle-2', 'bundle-1'].includes(
          cardData.cardData.value.type
        ) || isCMLLab
          ? true
          : isLocked
      "
      :vendor="cardData.cardData.value.vendor"
      :author="cardData.authorName.value"
      :date="eventDate"
      :list="props.cardStyle == 'Grid' ? false : true"
      :data-cy="props.cy"
      :link="props.link || undefined"
      :price="pricing.price.value"
      :listprice="pricing.listprice.value"
      :saletext="pricing.salePercentage.value"
      :retired="cardData.retiredContent.value"
      :nofooter="cardData.storePlansPage.value"
      :footerDesc="footerDescription"
      :store="cardData.storePlansPage.value"
      :purchaseType="cardPurchaseType"
      @cu-card.prevent="cardActions.handleClick"
      @cu-card-bookmark.prevent="$emit('bookmark', props.result)"
      @cu-card-cta.prevent="$emit('ctaClick', props.result)"
      @cu-card-remove.prevent="$emit('removeProgress', props.result)"
    >
      <div slot="tags">
        <tag-list
          :content="props.result"
          :isRevup="revUpCampaign.isRevup.value"
          :price-details="pricing.priceDetails.value"
          :showRevUpRecertCampaign="revUpCampaign.showRevUpRecertCampaign.value"
        />
      </div>

      <div
        v-if="
          cardData.enableCertifications.value &&
          cardData.resultCertifications.value &&
          cardData.cardData.value.type !== 'exam-voucher' &&
          (cardData.cardData.value.type !== 'lab' || isGradedLab)
        "
        slot="related-certs"
      >
        {{ cardData.resultCertifications.value }}
      </div>

      <div slot="heading">
        <RawHtmlBlock
          :content="cardData.displayName.value"
          class="line-clamp-3"
        />
        <span
          v-if="
            cardData.cardData.value?.type == 'learning-path' &&
            props.result?.acronym
          "
        >
          &nbsp;| {{ " " + props.result?.acronym }}
        </span>
      </div>
      <div v-if="props.showDescription" slot="description">
        {{ props.result.summary }}
      </div>
      <div slot="garden">
        <cu-icon-garden
          v-for="garden in filteredGarden"
          :key="garden.type"
          :type="cardIcons.iconType(garden.type)"
          :text="garden.text"
          :style="garden.style"
          horizontal="true"
        >
          <component
            :is="cardIcons.iconComponent(garden.type)"
            v-if="cardIcons.iconComponent(garden.type)"
            class="icon"
          ></component>

          <span
            v-if="cardIcons.isOneOfSpecificType(garden.type)"
            class="text"
            >{{ garden.text }}</span
          >
        </cu-icon-garden>
      </div>
      <div
        v-if="props.isAdminContent"
        slot="admin-tooltip"
        class="leading-normal tracking-wider"
      >
        As an admin, you can manage and remove your assigned learning through
        the
        <AnchorTargetBlank
          class="underline"
          :url="cardData.customerAdminLink.value"
          >Customer Admin Portal</AnchorTargetBlank
        >
      </div>
      <span slot="currency-type" v-if="pricing.price.value">
        <cu-tags text="USD" size="m" bgColor="gray" />
      </span>
      <cu-button
        v-if="pricing.price.value"
        slot="actions"
        data-cy="card-add-to-cart"
        template="primary"
        @click.prevent="
          cardActions.addProduct(
            pricing.priceDetails.value.productGuid.toString()
          )
        "
        size="s"
        >Add to cart</cu-button
      >
    </cu-new-card>
    <cu-new-card
      v-if="cardActions.isInViewPort.value && props.isUnauth"
      :class="
        cardData.storePlansPage.value
          ? 'h-auto'
          : props.forCarousel
            ? 'h-full min-h-[22rem]'
            : 'h-[22rem]'
      "
      :type="cardData.cardData.value.type"
      nobookmark=""
      :progress="progressPercentage || revUpCampaign.freeUntil.value"
      :premium="
        ['exam-voucher', 'bundle', 'bundle-3', 'bundle-2', 'bundle-1'].includes(
          cardData.cardData.value.type
        ) || isCMLLab
          ? true
          : isLocked
      "
      :vendor="cardData.cardData.value.vendor"
      :author="cardData.authorName.value"
      :date="eventDate"
      :list="props.cardStyle == 'Grid' ? false : true"
      :data-cy="props.cy"
      :link="props.link || undefined"
      :price="pricing.price.value"
      :listprice="pricing.listprice.value"
      :saletext="pricing.salePercentage.value"
      :nofooter="cardData.storePlansPage.value"
      :footerDesc="footerDescription"
      :store="cardData.storePlansPage.value"
      :purchaseType="cardPurchaseType"
      @click.prevent="false"
      @cu-card.prevent="$emit('navigate', props.result)"
      @cu-card-bookmark.prevent="$emit('bookmark', props.result)"
      @cu-card-cta.prevent="$emit('ctaClick', props.result)"
    >
      <div slot="tags">
        <tag-list
          :content="props.result"
          :isRevup="revUpCampaign.isRevup.value"
          :price-details="pricing.priceDetails.value"
          :showRevUpRecertCampaign="revUpCampaign.showRevUpRecertCampaign.value"
        />
      </div>

      <div
        v-if="
          cardData.enableCertifications.value &&
          cardData.resultCertifications.value &&
          cardData.cardData.value.type !== 'exam-voucher' &&
          (cardData.cardData.value.type !== 'lab' || isGradedLab)
        "
        slot="related-certs"
      >
        {{ cardData.resultCertifications.value }}
      </div>

      <div slot="heading">
        <RawHtmlBlock :content="cardData.displayName.value" class="inline" />
        <span
          v-if="
            cardData.cardData.value?.type == 'learning-path' &&
            props.result?.acronym
          "
        >
          &nbsp;| {{ " " + props.result?.acronym }}
        </span>
      </div>
      <div v-if="props.showDescription" slot="description">
        {{ props.result.summary }}
      </div>
      <div slot="garden">
        <cu-icon-garden
          v-for="garden in filteredGarden"
          :key="garden.type"
          :type="cardIcons.iconType(garden.type)"
          :text="garden.text"
          :style="garden.style"
          horizontal="true"
        >
          <component
            :is="cardIcons.iconComponent(garden.type)"
            v-if="cardIcons.iconComponent(garden.type)"
            class="icon"
          ></component>

          <span
            v-if="cardIcons.isOneOfSpecificType(garden.type)"
            class="text"
            >{{ garden.text }}</span
          >
        </cu-icon-garden>
      </div>
      <span slot="currency-type" v-if="pricing.price.value">
        <cu-tags text="USD" size="m" bgColor="gray" />
      </span>
      <cu-button
        v-if="pricing.price.value"
        slot="actions"
        template="primary"
        data-cy="card-signin-to-buy"
        @click.prevent="
          cardActions.navigateToLogin(cardData.routerName.value, props.link)
        "
        size="s"
        >Sign in to buy</cu-button
      >
    </cu-new-card>
  </div>
</template>

<style scoped>
[type="time"] {
  margin-right: 0.5rem;
  border: none;
  padding: 0;
  line-height: normal;
  background: none;
}
</style>
